import XLSX from 'xlsx';
import { PretensionReportApiRow, formatDateTimeDdMmYyyyHhMm } from './pretensionsReportDisplay';
import { formatDateDdMmYyyy } from './operationReportDisplay';

const SHEET_PRETENSIONS = 'Претензии';
const SHEET_HISTORY = 'История статусов';

// Колонки = колонки UI-таблицы отчёта (включая ID первым).
const HEADERS_PRETENSIONS = [
  'ID', '№', 'Тип', 'Платформа', 'Поставщик',
  'Дата создания', 'Сумма (без НДС)',
  'Текущий статус',
] as const;

const HISTORY_SUB_HEADER = ['Этап', 'Дата', 'Кем', 'Комментарий'] as const;

const COL_WIDTHS_PRETENSIONS = [10, 16, 10, 24, 32, 14, 18, 28];
const COL_WIDTHS_HISTORY = [60, 18, 32, 60];

function setNumericCells(ws: XLSX.WorkSheet, columnLetters: string[], lastRow: number) {
  for (let r = 2; r <= lastRow; r += 1) {
    columnLetters.forEach((col) => {
      const ref = `${col}${r}`;
      const cell = ws[ref];
      if (cell && typeof cell.v === 'number') cell.t = 'n';
    });
  }
}

function buildHistorySheet(
  rows: PretensionReportApiRow[],
  i18nStatus: (s: string) => string,
): XLSX.WorkSheet {
  // Grouped layout: один блок на претензию.
  // === № <number> | Поставщик: <supplier> | Создана: <date> ===
  // [Этап, Дата, Кем, Комментарий]
  // <transitions>
  // (blank row separator перед следующим блоком)
  // Пустая история → заголовок + строка "(нет переходов)" (без sub-header).
  const aoa: (string | number | null)[][] = [];
  rows.forEach((r, idx) => {
    if (idx > 0) aoa.push(['', '', '', '']);
    const title = `=== № ${r.number} | Поставщик: ${r.supplierLegalName ?? '—'} | Создана: ${formatDateDdMmYyyy(r.createdAt)} ===`;
    aoa.push([title, '', '', '']);
    if (r.statusHistory.length === 0) {
      aoa.push(['(нет переходов)', '', '', '']);
      return;
    }
    aoa.push([...HISTORY_SUB_HEADER]);
    r.statusHistory.forEach((h) => {
      const fromLabel = h.fromStatus ? i18nStatus(h.fromStatus) : '(создание)';
      const toLabel = i18nStatus(h.toStatus);
      aoa.push([
        `${fromLabel} → ${toLabel}`,
        formatDateTimeDdMmYyyyHhMm(h.changedAt),
        h.changedBy || 'system',
        h.comment ?? '—',
      ]);
    });
  });
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = COL_WIDTHS_HISTORY.map((wch) => ({ wch }));
  return ws;
}

export function buildPretensionsReportWorkbook(
  rows: PretensionReportApiRow[],
  i18nStatus: (s: string) => string,
  i18nType: (t: string) => string,
): XLSX.WorkBook {
  const aoa1: (string | number | null)[][] = [Array.from(HEADERS_PRETENSIONS)];
  rows.forEach((r) => {
    aoa1.push([
      r.id,
      r.number,
      i18nType(r.type),
      r.platformName ?? '',
      r.supplierLegalName ?? '',
      formatDateDdMmYyyy(r.createdAt),
      r.totalWithoutTax,
      i18nStatus(r.currentStatus),
    ]);
  });
  const ws1 = XLSX.utils.aoa_to_sheet(aoa1);
  ws1['!cols'] = COL_WIDTHS_PRETENSIONS.map((wch) => ({ wch }));
  // Числовые: ID (A) и Сумма (G). Порядок: A=ID, B=№, …, F=Дата, G=Сумма, H=Статус.
  setNumericCells(ws1, ['A', 'G'], aoa1.length);

  const ws2 = buildHistorySheet(rows, i18nStatus);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws1, SHEET_PRETENSIONS);
  XLSX.utils.book_append_sheet(wb, ws2, SHEET_HISTORY);
  return wb;
}
